import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, MessageCircle, Phone } from "lucide-react";
import { cn } from "@/lib/utils";

export function CommunicationSetting() {
  const [emailChecked, setEmailChecked] = useState(false);
  const [smsChecked, setSmsChecked] = useState(false);
  const [whatsappChecked, setWhatsappChecked] = useState(false);

  const [emailData, setEmailData] = useState({
    emailId: "",
    smtp: "",
    id: "",
    password: "",
  });

  const [smsData, setSmsData] = useState({
    url: "",
    username: "",
    password: "",
    senderId: "",
    apiKey: "",
  });

  const [whatsappData, setWhatsappData] = useState({
    url: "",
    username: "",
    password: "",
    apiKey: "",
  });

  const handleEmailSave = () => {
    // Handle email save logic here
    console.log("Email settings saved:", emailData);
  };

  const handleSmsSave = () => {
    // Handle SMS save logic here
    console.log("SMS settings saved:", smsData);
  };

  const handleWhatsappSave = () => {
    // Handle WhatsApp save logic here
    console.log("WhatsApp settings saved:", whatsappData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Channel selection - custom checkbox cards similar to Campaign Type */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Email */}
          <button
            type="button"
            onClick={() => setEmailChecked((prev) => !prev)}
            className={cn(
              "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors w-full text-left",
              emailChecked
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            )}
          >
            <Checkbox
              id="email"
              checked={emailChecked}
              onCheckedChange={(checked) => setEmailChecked(checked as boolean)}
              className="sr-only"
            />
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-100 text-blue-600">
              <Mail className="h-5 w-5" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold">Email</span>
              <span className="text-xs text-muted-foreground">
                Configure SMTP and email credentials.
              </span>
            </div>
          </button>

          {/* SMS */}
          <button
            type="button"
            onClick={() => setSmsChecked((prev) => !prev)}
            className={cn(
              "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors w-full text-left",
              smsChecked
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            )}
          >
            <Checkbox
              id="sms"
              checked={smsChecked}
              onCheckedChange={(checked) => setSmsChecked(checked as boolean)}
              className="sr-only"
            />
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-indigo-100 text-indigo-600">
              <Phone className="h-5 w-5" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold">SMS</span>
              <span className="text-xs text-muted-foreground">
                Configure SMS gateway and sender ID.
              </span>
            </div>
          </button>

          {/* WhatsApp */}
          <button
            type="button"
            onClick={() => setWhatsappChecked((prev) => !prev)}
            className={cn(
              "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors w-full text-left",
              whatsappChecked
                ? "border-green-500 bg-emerald-50"
                : "border-gray-200 hover:border-gray-300"
            )}
          >
            <Checkbox
              id="whatsapp"
              checked={whatsappChecked}
              onCheckedChange={(checked) => setWhatsappChecked(checked as boolean)}
              className="sr-only"
            />
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-100 text-emerald-600">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold">WhatsApp</span>
              <span className="text-xs text-muted-foreground">
                Configure WhatsApp API credentials.
              </span>
            </div>
          </button>
        </div>

        {/* Email Fields */}
        {emailChecked && (
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700">Email Configuration</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="email-id">Email Id</Label>
                <Input
                  id="email-id"
                  type="email"
                  value={emailData.emailId}
                  onChange={(e) =>
                    setEmailData({ ...emailData, emailId: e.target.value })
                  }
                  placeholder="Enter email ID"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp">SMTP</Label>
                <Input
                  id="smtp"
                  type="text"
                  value={emailData.smtp}
                  onChange={(e) =>
                    setEmailData({ ...emailData, smtp: e.target.value })
                  }
                  placeholder="Enter SMTP"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-username">ID</Label>
                <Input
                  id="email-username"
                  type="text"
                  value={emailData.id}
                  onChange={(e) =>
                    setEmailData({ ...emailData, id: e.target.value })
                  }
                  placeholder="Enter ID"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-password">Password</Label>
                <Input
                  id="email-password"
                  type="password"
                  value={emailData.password}
                  onChange={(e) =>
                    setEmailData({ ...emailData, password: e.target.value })
                  }
                  placeholder="Enter password"
                />
              </div>
            </div>
            <Button onClick={handleEmailSave} className="bg-green-600 hover:bg-green-700">
              Save
            </Button>
          </div>
        )}

        {/* SMS Fields */}
        {smsChecked && (
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700">SMS Configuration</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="sms-url">URL</Label>
                <Input
                  id="sms-url"
                  type="text"
                  value={smsData.url}
                  onChange={(e) =>
                    setSmsData({ ...smsData, url: e.target.value })
                  }
                  placeholder="Enter URL"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sms-username">Username</Label>
                <Input
                  id="sms-username"
                  type="text"
                  value={smsData.username}
                  onChange={(e) =>
                    setSmsData({ ...smsData, username: e.target.value })
                  }
                  placeholder="Enter username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sms-password">Password</Label>
                <Input
                  id="sms-password"
                  type="password"
                  value={smsData.password}
                  onChange={(e) =>
                    setSmsData({ ...smsData, password: e.target.value })
                  }
                  placeholder="Enter password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sender-id">Sender Id</Label>
                <Input
                  id="sender-id"
                  type="text"
                  value={smsData.senderId}
                  onChange={(e) =>
                    setSmsData({ ...smsData, senderId: e.target.value })
                  }
                  placeholder="Enter sender ID"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sms-api-key">API Key</Label>
                <Input
                  id="sms-api-key"
                  type="text"
                  value={smsData.apiKey}
                  onChange={(e) =>
                    setSmsData({ ...smsData, apiKey: e.target.value })
                  }
                  placeholder="Enter API key"
                />
              </div>
            </div>
            <Button onClick={handleSmsSave} className="bg-green-600 hover:bg-green-700">
              Save
            </Button>
          </div>
        )}

        {/* WhatsApp Fields */}
        {whatsappChecked && (
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700">WhatsApp Configuration</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="whatsapp-url">URL</Label>
                <Input
                  id="whatsapp-url"
                  type="text"
                  value={whatsappData.url}
                  onChange={(e) =>
                    setWhatsappData({ ...whatsappData, url: e.target.value })
                  }
                  placeholder="Enter URL"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp-username">Username</Label>
                <Input
                  id="whatsapp-username"
                  type="text"
                  value={whatsappData.username}
                  onChange={(e) =>
                    setWhatsappData({ ...whatsappData, username: e.target.value })
                  }
                  placeholder="Enter username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp-password">Password</Label>
                <Input
                  id="whatsapp-password"
                  type="password"
                  value={whatsappData.password}
                  onChange={(e) =>
                    setWhatsappData({ ...whatsappData, password: e.target.value })
                  }
                  placeholder="Enter password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp-api-key">API Key</Label>
                <Input
                  id="whatsapp-api-key"
                  type="text"
                  value={whatsappData.apiKey}
                  onChange={(e) =>
                    setWhatsappData({ ...whatsappData, apiKey: e.target.value })
                  }
                  placeholder="Enter API key"
                />
              </div>
            </div>
            <Button onClick={handleWhatsappSave} className="bg-green-600 hover:bg-green-700">
              Save
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
